import React, { createContext, ReactElement, useContext } from 'react';
import { useBrowserOrAccountLocale } from '../hooks/useBrowserOrAccountLocale';
import { supportedL10n } from './localization';

// Context
export type I18nContextType = { langBundle: any; defaultBundle: any };
export const I18nContext = createContext<I18nContextType>(undefined);
type I18nProviderProps = { langBundle: any; defaultBundle; children: React.ReactNode };
export const I18nProvider = ({ langBundle, defaultBundle, children }: I18nProviderProps) => {
    const locale = useBrowserOrAccountLocale();

    const hookLangBundle = supportedL10n[locale]?.appBundle;
    const effectiveLangBundle = hookLangBundle || langBundle;

    return (
        <I18nContext.Provider value={{ langBundle: effectiveLangBundle, defaultBundle }}>
            {children}
        </I18nContext.Provider>
    );
};

export type SubstitutionMapType = {
    [key: string]: string | number | boolean | ReactElement;
};
type I18NProps = {
    token: string;
    values?: SubstitutionMapType;
};

export default function I18n(props: I18NProps): ReactElement {
    const { token, values = undefined } = props;
    return <>{useI18n(token, values)}</>;
}

const resolveI18nToken = (
    context: I18nContextType,
    token: string,
    values?: { [p: string]: string | number | boolean }
) => {
    let val = context.langBundle[token] ? context.langBundle[token] : context.defaultBundle[token];
    if (val) {
        if (values) {
            for (const key in values) {
                val = val.replaceAll('{' + key + '}', values[key]);
            }
        }
    } else {
        console.warn(`Please add ${token} to language file`);
    }
    return val || token;
};

export type I18nFunctionType = (token: string, values?: SubstitutionMapType) => string;

export function useI18n(): I18nFunctionType;
export function useI18n(
    token: Parameters<I18nFunctionType>[0],
    values?: Parameters<I18nFunctionType>[1]
): ReturnType<I18nFunctionType>;

export function useI18n(
    token?: Parameters<I18nFunctionType>[0],
    values?: Parameters<I18nFunctionType>[1]
): string | I18nFunctionType {
    const context: I18nContextType = useContext(I18nContext);

    const translate = (t: string, v?: SubstitutionMapType): string => {
        if (typeof v === 'string') {
            console.error('Translation function signature requires 2nd argument to be object, not string');
        }
        return resolveI18nToken(context, t, v);
    };

    // Option 1: Called without arguments - return the translation function
    if (token === undefined) {
        return translate;
    }

    // Option 2: Called with token - return the translated string directly
    return translate(token, values);
}

export const getI18nFunction = (context: I18nContextType) => {
    return (token, values) => {
        return resolveI18nToken(context, token, values);
    };
};
